import * as React from 'react';

import { LucideIcon, XIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface FilterItemProps extends React.ComponentPropsWithoutRef<typeof Button> {
  icon?: LucideIcon;
  title: string;
  text: string;
  onClose?: () => void;
  isActive?: boolean;
}

/**
 * Reusable component for displaying filter items with icon, text, and optional close button.
 */
export const FilterItem = ({
  icon: Icon,
  title,
  text,
  onClose,
  isActive = false,
  className,
  ref,
  ...rest
}: FilterItemProps & { ref?: React.Ref<HTMLButtonElement> }) => {
  return (
    <Button
      ref={ref}
      variant="secondary"
      size="sm"
      className={cn(
        'h-auto min-h-6 min-w-0 justify-start gap-2 py-1.5 font-normal text-foreground hover:bg-secondary/80 [&_svg]:text-muted-foreground',
        isActive && 'ring-2 ring-selected ring-offset-1',
        className
      )}
      {...rest}
    >
      {Icon ? <Icon size={16} className="text-muted-foreground" /> : null}

      <div className="truncate text-sm">
        <span className="font-semibold">{title}: </span>
        <span className="font-normal">{text}</span>
      </div>

      {onClose && (
        <Button
          variant="ghost-neutral"
          size="icon-xss"
          onClick={(event) => {
            event.stopPropagation();
            onClose();
          }}
        >
          <XIcon size={16} />
        </Button>
      )}
    </Button>
  );
};
